// src/layouts/AppLayout.tsx
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { TopNavigation } from '@/components/navigation/top-navigation'
import { CreditCard, ChevronLeft, ChevronRight, X, Zap } from 'lucide-react'
import { useAuth } from '@/components/providers/auth-provider'
import { useState, useEffect } from 'react'
import { BriefingStackIcon } from '@/components/nav-icons/BriefingStackIcon'
import { DashIcon } from '@/components/nav-icons/DashIcon'
import { PreferencesIcon } from '@/components/nav-icons/PreferencesPuzzleIcon'
import { SidebarNavLink } from '@/components/navigation/SidebarNavLink'
import { SettingsCogIcon } from '@/components/nav-icons/SettingsCogIcon.tsx'
import { VaultIcon } from '@/components/nav-icons/VaultIcon.tsx'
import { BetaFeatureModal } from '@/components/BetaFeatureModal'
import { AudioProvider } from '@/components/providers/AudioProvider'
import { MiniPlayerBar } from '@/components/audio/MiniPlayerBar'
import { useAudio } from '@/components/providers/AudioProvider'

declare global {
  interface Window { $crisp: any[]; CRISP_WEBSITE_ID: string }
}

const BETA_BANNER_KEY = 'digestr_beta_banner_dismissed'

const navItems = [
  { label: 'Dashboard', href: '/app/dashboard', icon: DashIcon },
  { label: 'Briefings', href: '/app/briefings', icon: BriefingStackIcon },
  { label: 'Vault', href: '/app/vault', icon: VaultIcon },
  { label: 'Preferences', href: '/app/preferences', icon: PreferencesIcon },
  { label: 'Settings', href: '/app/settings', icon: SettingsCogIcon },
]

// Mobile bottom nav items — trim to 4 for thumb reach
const mobileNavItems = [
  { label: 'Dashboard', href: '/app/dashboard', icon: DashIcon },
  { label: 'Briefings', href: '/app/briefings', icon: BriefingStackIcon },
  { label: 'Preferences', href: '/app/preferences', icon: PreferencesIcon },
  { label: 'Settings', href: '/app/settings', icon: SettingsCogIcon },
]

// ─── Mobile bottom nav ────────────────────────────────────────────────────────

function MobileBottomNav({ activePath }: { activePath: string }) {
  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
      background: 'var(--color-background-primary)',
      borderTop: '0.5px solid var(--color-border-tertiary)',
      display: 'flex', alignItems: 'stretch',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {/* Top gradient line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg, rgba(102,0,255,0.3), rgba(0,255,192,0.15), transparent)',
        pointerEvents: 'none',
      }} />

      {mobileNavItems.map(({ label, href, icon: Icon }) => {
        const isActive = activePath.startsWith(href)
        return (
          <Link
            key={href}
            to={href}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 4, padding: '10px 4px 8px', textDecoration: 'none',
              color: isActive ? '#6600FF' : 'var(--color-text-secondary)',
              position: 'relative',
            }}
          >
            {/* Active indicator dot */}
            {isActive && (
              <div style={{
                position: 'absolute', top: 0, left: '25%', right: '25%', height: 2,
                background: 'linear-gradient(90deg, #6600FF, rgba(0,255,192,0.8))',
                borderRadius: '0 0 2px 2px',
              }} />
            )}
            <Icon className={`w-5 h-5 ${isActive ? 'text-digestr-500' : 'text-muted-foreground'}`} />
            <span style={{
              fontSize: 10, fontWeight: isActive ? 500 : 400,
              color: isActive ? '#6600FF' : 'var(--color-text-secondary)',
            }}>
              {label}
            </span>
          </Link>
        )
      })}
    </nav>
  )
}

// ─── Inner layout ─────────────────────────────────────────────────────────────

function AppLayoutInner() {
  const { user } = useAuth()
  const location = useLocation()
  const { activeBriefing } = useAudio()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [betaBannerVisible, setBetaBannerVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    const dismissed = localStorage.getItem(BETA_BANNER_KEY)
    if (!dismissed) setBetaBannerVisible(true)
  }, [])

  const dismissBetaBanner = () => {
    localStorage.setItem(BETA_BANNER_KEY, 'true')
    setBetaBannerVisible(false)
  }

  useEffect(() => {
    window.$crisp = []
    window.CRISP_WEBSITE_ID = 'cca3be81-b781-4c46-b55e-b9fcc11dd2f1'
    const script = document.createElement('script')
    script.src = 'https://client.crisp.chat/l.js'
    script.async = true
    document.head.appendChild(script)
    return () => { document.head.removeChild(script) }
  }, [])

  const isActive = (href: string) => location.pathname.startsWith(href)

  const handleOpenChat = () => {
    if (window.$crisp) window.$crisp.push(['do', 'chat:open'])
  }

  // Bottom padding: mobile nav (56px) + mini player (72px) if active | desktop: mini player only
  const mainPaddingBottom = isMobile
    ? activeBriefing ? '128px' : '56px'
    : activeBriefing ? '72px' : undefined

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation />

      {/* Beta banner */}
      {betaBannerVisible && (
        <div className="bg-muted/60 border-b border-border px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 text-sm text-muted-foreground min-w-0">
              <Zap className="w-4 h-4 flex-shrink-0 text-digestr-500" />
              <span>
                <span className="font-semibold text-foreground">Digestr is in public beta.</span>
                {' '}You may encounter rough edges — we're improving fast.{' '}
                <button onClick={handleOpenChat} className="underline underline-offset-2 hover:text-foreground transition-colors">
                  Share feedback
                </button>
              </span>
            </div>
            <button onClick={dismissBetaBanner} className="flex-shrink-0 p-1 rounded hover:bg-muted text-muted-foreground transition-colors" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex" style={{ height: `calc(100vh - 64px${betaBannerVisible ? ' - 41px' : ''})` }}>

        {/* Desktop sidebar — hidden on mobile */}
        {!isMobile && (
          <aside className={`flex flex-col ${sidebarCollapsed ? 'w-20' : 'w-64'} bg-card border-r border-border overflow-y-auto transition-all duration-300 ease-in-out`}>
            <div className="flex items-center justify-end p-4 border-b border-border">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>

            <nav className="flex-1 p-4 space-y-1">
              {navItems.map(item => (
                <SidebarNavLink
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  label={item.label}
                  isActive={isActive(item.href)}
                  collapsed={sidebarCollapsed}
                />
              ))}
            </nav>

            {/* Upgrade prompt — expanded */}
            {!sidebarCollapsed && user?.subscription_tier === 'free' && (
              <div className="mx-4 mb-3 rounded-lg border border-border overflow-hidden" style={{ position: 'relative' }}>
                <div style={{ height: 1.5, background: 'linear-gradient(90deg, #6600FF, rgba(0,255,192,0.6), transparent 80%)' }} />
                <div className="p-4 bg-card">
                  <p className="text-sm font-semibold text-foreground mb-1">Upgrade to Starter</p>
                  <p className="text-xs text-muted-foreground mb-3">More briefings and advanced features</p>
                  <Link to="/billing" className="flex items-center justify-center gap-2 text-sm btn-gradient px-3 py-2">
                    <CreditCard className="w-4 h-4" />
                    View Plans
                  </Link>
                </div>
              </div>
            )}

            {/* Upgrade prompt — collapsed */}
            {sidebarCollapsed && user?.subscription_tier === 'free' && (
              <div className="mx-auto mb-3">
                <Link to="/billing" className="flex items-center justify-center w-12 h-12 rounded-lg bg-digestr-500/10 hover:bg-digestr-500/20 text-digestr-500 transition-colors" title="Upgrade Plan">
                  <CreditCard className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Chat button — expanded */}
            {!sidebarCollapsed && (
              <div className="mx-4 mb-3">
                <button onClick={handleOpenChat} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-muted transition-colors text-sm text-muted-foreground hover:text-foreground">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>Chat with us</span>
                  <span className="ml-auto w-2 h-2 rounded-full bg-green-500" title="Online" />
                </button>
              </div>
            )}

            {/* Chat button — collapsed */}
            {sidebarCollapsed && (
              <div className="mx-auto mb-3">
                <button onClick={handleOpenChat} className="relative flex items-center justify-center w-12 h-12 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Chat with us">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-500" />
                </button>
              </div>
            )}

            {/* Tier badge */}
            <div className={`mx-4 mb-4 p-3 bg-muted rounded-lg ${sidebarCollapsed ? 'text-center' : ''}`}>
              {!sidebarCollapsed ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'linear-gradient(135deg, #6600FF, #00FFC0)', flexShrink: 0 }} />
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.03em' }}>current plan</p>
                    <p className="text-sm font-medium text-foreground capitalize">{user?.subscription_tier || 'Free'}</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs font-bold text-digestr-500 capitalize">
                  {user?.subscription_tier?.[0]?.toUpperCase() || 'F'}
                </p>
              )}
            </div>
          </aside>
        )}

        {/* Main content */}
        <main
          className="flex-1 overflow-auto bg-background"
          style={{ paddingBottom: mainPaddingBottom }}
        >
          {/* Mobile: no inner padding wrapper — dashboard-page handles its own */}
          {isMobile ? (
            <Outlet />
          ) : (
            <div className="max-w-7xl mx-auto p-4 md:p-8">
              <Outlet />
            </div>
          )}
        </main>
      </div>

      {/* Mini player — above mobile nav */}
      <MiniPlayerBar />

      {/* Mobile bottom nav */}
      {isMobile && <MobileBottomNav activePath={location.pathname} />}

      <BetaFeatureModal />
    </div>
  )
}

export function AppLayout() {
  return (
    <AudioProvider>
      <AppLayoutInner />
    </AudioProvider>
  )
}